import type { Rating, RatingHistoryEntry } from "../types/rating";
import type { DateValue } from "../types/data";
import { isUnknownArray, isTimestamp } from "../types/data";
import type { EntityId } from "../types/media";
import type {
  DungeonCatalogValidation, DungeonCondition, DungeonRarity, DungeonReconciliation, DungeonReconciliationDefinition,
  DungeonStageCriteria, DungeonStageDefinition, DungeonStageProgress, GlobalWeeklyDungeons,
  WeeklyDungeon, WeeklyDungeonState
} from "../types/games";
import {
  hasImportMarker,
  isGameProgressEligible,
  isShortFilm
} from "./gameProgressEligibility";
import {
  getCurrentWeekId,
  getWeekEnd,
  getWeekStart
} from "./weeklyQuestCards";
import { countryBelongsToGroup } from "./countryGroups";
import {
  getRatingActors,
  getRatingComposers,
  getRatingDirectors,
  personMatchesCondition
} from "./ratingPeople";

export { getCurrentWeekId, getWeekEnd, getWeekStart };

export const WEEKLY_DUNGEON_GENERATOR_VERSION = 1;
export const WEEKLY_DUNGEON_STATE_VERSION = 2;
export const WEEKLY_DUNGEON_EPIC_CHANCE = 0.2;
export const WEEKLY_DUNGEON_LEGENDARY_BASE_CHANCE = 0.05;
export const WEEKLY_DUNGEON_LEGENDARY_PITY_STEP = 0.05;
export const WEEKLY_DUNGEON_BACKDATE_DAYS = 1;

export const WEEKLY_DUNGEON_RARITIES = {
  common: { label: "Обычная", reward: 80, order: 1 },
  epic: { label: "Эпическая", reward: 350, order: 2 },
  legendary: { label: "Легендарная", reward: 600, order: 3 }
};

const LEGACY_DUNGEON_STAGE_DESCRIPTIONS: Record<string, string> = {
  "Посмотрите 12 фильмов базовых жанров.":
    "Посмотрите 12 фильмов жанров: драма, комедия, фантастика или мультфильм."
};

const normalizeDungeonStageDescription = (description: string | undefined) =>
  LEGACY_DUNGEON_STAGE_DESCRIPTIONS[String(description || "").trim()] || description;

export const getWeeklyDungeonReward = (rarity: string|number, fallback = 0) => {
  const configuredReward = Number(WEEKLY_DUNGEON_RARITIES[rarity as DungeonRarity]?.reward);
  if (Number.isFinite(configuredReward)) return configuredReward;
  const fallbackReward = Number(fallback);
  return Number.isFinite(fallbackReward) ? fallbackReward : 0;
};

export const normalizeWeeklyDungeonReward = (dungeon: WeeklyDungeon | null | undefined) => {
  if (!dungeon) return null;
  const rarityMeta = WEEKLY_DUNGEON_RARITIES[dungeon.rarity] || {};
  return {
    ...dungeon,
    rarityLabel: rarityMeta.label || dungeon.rarityLabel,
    reward: getWeeklyDungeonReward(dungeon.rarity, dungeon.reward),
    stages: isUnknownArray(dungeon.stages)
      ? dungeon.stages.map((stage) => ({
          ...stage,
          description: normalizeDungeonStageDescription(stage?.description)
        }))
      : dungeon.stages
  };
};

export const WEEKLY_DUNGEON_STAGE_RULES: Record<DungeonRarity, DungeonStageDefinition[]> = {
  common: [
    { id: "stage-1", title: "Этап 1", requiredMoviesCount: 1, durationDays: 2 },
    { id: "stage-2", title: "Этап 2", requiredMoviesCount: 1, durationDays: 2 },
    { id: "boss", title: "Финальный этап", requiredMoviesCount: 3, durationDays: 3, boss: true }
  ],
  epic: [
    { id: "stage-1", title: "Этап 1", requiredMoviesCount: 3, durationDays: 2 },
    { id: "stage-2", title: "Этап 2", requiredMoviesCount: 5, durationDays: 2 },
    { id: "boss", title: "Финальный этап", requiredMoviesCount: 8, durationDays: 3, boss: true }
  ],
  legendary: [
    { id: "stage-1", title: "Этап 1", requiredMoviesCount: 5, durationDays: 2 },
    { id: "stage-2", title: "Этап 2", requiredMoviesCount: 8, durationDays: 2 },
    { id: "boss", title: "Финальный этап", requiredMoviesCount: 12, durationDays: 3, boss: true }
  ]
};

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value ? [value] : [];
const normalizeText = (value: unknown) => String(value || "").toLowerCase().replace(/ё/g, "е").trim();
const normalizeId = (value: unknown) => String(value || "").toLowerCase().replace(/[^a-z0-9а-яё]+/gi, "-").replace(/^-|-$/g, "");

const getGenres = (rating: Rating) => asArray(rating?.genres || rating?.filmGenres)
  .map((item) => normalizeText(typeof item === "string" ? item : item?.name))
  .filter(Boolean);

const getCountries = (rating: Rating) => [
  ...asArray(rating?.originCountry),
  ...asArray(rating?.productionCountry),
  ...asArray(rating?.productionCountries)
].map((item) => normalizeText(typeof item === "string" ? item : item?.name || item?.iso_3166_1)).filter(Boolean);

const getYear = (rating: Rating) => {
  const raw = rating?.releaseYear || rating?.releaseDate || rating?.filmReleaseDate || rating?.release_date || rating?.first_air_date;
  const year = Number(String(raw || "").slice(0, 4));
  return Number.isFinite(year) ? year : null;
};

const dungeonTimestampMillis = (value: DateValue | undefined): number => {
  const date = isTimestamp(value) ? value.toDate() : value instanceof Date ? new Date(value) :
    value && (typeof value === "string" || typeof value === "number") ? new Date(value) : null;
  const time = date?.getTime?.();
  return time !== undefined && Number.isFinite(time) ? time : 0;
};

export const getDungeonRatingTime = (rating: Rating) => {
  const value = rating?.firstTrackedAt || rating?.firstRatedAt || rating?.ratedAt;
  return dungeonTimestampMillis(value);
};

const getDungeonWatchedTime = (rating: Rating) => {
  const value = rating?.firstRatedAt || rating?.ratedAt;
  return dungeonTimestampMillis(value);
};

const getDungeonRatingUpdatedTime = (rating: Rating) => {
  const value = rating?.updatedAt || rating?.lastRatedAt || rating?.ratedAt || rating?.firstTrackedAt;
  return dungeonTimestampMillis(value);
};

const getDungeonDeletionUpdatedTime = (rating: Rating) => {
  const value = rating?.updatedAt || rating?.deletedAt || rating?.lastRatedAt || rating?.ratedAt || rating?.firstTrackedAt;
  return dungeonTimestampMillis(value);
};

const getLocalCalendarDayNumber = (time: unknown) => {
  if (typeof time !== "number" || !Number.isFinite(time)) return null;
  const date = new Date(time);
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / (24 * 60 * 60 * 1000));
};

export const getDungeonRatingBackdateDays = (rating: Rating) => {
  if (!rating?.manualWatchedAt) return 0;
  const trackedDay = getLocalCalendarDayNumber(getDungeonRatingTime(rating));
  const watchedDay = getLocalCalendarDayNumber(getDungeonWatchedTime(rating));
  if (trackedDay == null || watchedDay == null) return Number.POSITIVE_INFINITY;
  return trackedDay - watchedDay;
};

export const isDungeonRatingTimingEligible = (
  rating: Rating,
  startedAt: number | null | undefined,
  expiresAt: number | null | undefined,
  { allowPreviouslyMatched = false }: { allowPreviouslyMatched?: boolean } = {}
) => {
  const trackedAt = getDungeonRatingTime(rating);
  const start = Number(startedAt) || 0;
  const end = Number(expiresAt) || Number.MAX_SAFE_INTEGER;

  // A manually selected viewing date never moves the submission itself back in
  // time: the immutable firstTrackedAt must still fall inside the active stage.
  if (!trackedAt || trackedAt < start || trackedAt >= end) return false;
  if (!rating?.manualWatchedAt) return true;
  // Deleting a rating starts a new lifecycle. If this exact movie had already
  // qualified for the stage, allow its new lifecycle to restore that earned
  // progress while the stage is still active. The submission time, stage
  // conditions and import/short-film guards are still enforced.
  if (allowPreviouslyMatched) return true;

  const backdateDays = getDungeonRatingBackdateDays(rating);
  return backdateDays >= 0 && backdateDays <= WEEKLY_DUNGEON_BACKDATE_DAYS;
};

const isDungeonProgressEligible = (rating: Rating) => {
  if (!rating || rating.active === false) return false;
  if (isGameProgressEligible(rating)) return true;

  // A date selected for yesterday is normally classified as archive progress.
  // Expeditions intentionally allow that one narrow case; imports and shorts
  // remain excluded, and the exact one-day window is checked with stage timing.
  return Boolean(
    rating.manualWatchedAt &&
    !hasImportMarker(rating) &&
    !isShortFilm(rating) &&
    (rating.firstProgressMode || rating.progressMode) === "archive"
  );
};

export const mergeDungeonRatings = (ratings: Rating[] = [], ratingHistory: RatingHistoryEntry[] = []) => {
  const liveById = new Map<string, Rating>();
  const historyById = new Map<string, RatingHistoryEntry>();

  asArray(ratings).forEach((rating: Rating) => {
    const filmId = rating?.filmId || rating?.id;
    if (filmId != null) liveById.set(String(filmId), rating);
  });
  asArray(ratingHistory).forEach((rating: Rating) => {
    const filmId = rating?.filmId || rating?.id;
    if (filmId != null) historyById.set(String(filmId), rating);
  });

  // The live ratings collection is authoritative for membership. History only
  // enriches a rating that is still live with immutable lifecycle metadata.
  // Processing history-only entries used to resurrect a just-deleted rating
  // for one render while the two Firestore listeners settled. That could
  // falsely complete challenges and expeditions before the tombstone arrived.
  return [...liveById.keys()]
    .map((filmId) => {
      const live = liveById.get(filmId);
      const history = historyById.get(filmId);
      const liveLifecycleId = String(live?.ratingLifecycleId || "");
      const historyLifecycleId = String(history?.ratingLifecycleId || "");
      const sameLifecycle =
        !liveLifecycleId ||
        !historyLifecycleId ||
        liveLifecycleId === historyLifecycleId;
      if (
        live &&
        history?.active === false &&
        sameLifecycle &&
        getDungeonDeletionUpdatedTime(history) > getDungeonRatingUpdatedTime(live)
      ) {
        return null;
      }

      const firstTrackedAt =
        live?.firstTrackedAt ||
        history?.firstTrackedAt ||
        live?.firstRatedAt ||
        history?.firstRatedAt ||
        live?.ratedAt ||
        history?.ratedAt ||
        null;
      const firstRatedAt =
        live?.firstRatedAt ||
        history?.firstRatedAt ||
        firstTrackedAt;

      return {
        ...(history || {}),
        ...(live || {}),
        filmId,
        ratingLifecycleId: live?.ratingLifecycleId || history?.ratingLifecycleId || null,
        firstTrackedAt,
        firstRatedAt,
        ratedAt: live?.ratedAt || history?.ratedAt || firstRatedAt,
        active: live ? live.active !== false : history?.active !== false
      };
    })
    .filter((rating): rating is NonNullable<typeof rating> => Boolean(rating) && rating?.active !== false);
};

const getDungeonRatingTimeLifecycleKey = (rating: Rating) => {
  const filmId = String(rating?.filmId || rating?.id || "");
  const firstTrackedAt = getDungeonRatingTime(rating);
  return filmId && firstTrackedAt ? `time:${filmId}:${firstTrackedAt}` : "";
};

export const getDungeonRatingLifecycleKey = (rating: Rating) => {
  const filmId = String(rating?.filmId || rating?.id || "");
  const lifecycleId = String(rating?.ratingLifecycleId || "");
  return filmId && lifecycleId
    ? `id:${filmId}:${lifecycleId}`
    : getDungeonRatingTimeLifecycleKey(rating);
};

const getComparableDungeonLifecycleKey = (rating: Rating, originalKey: string) => {
  if (String(originalKey).startsWith("time:")) {
    return getDungeonRatingTimeLifecycleKey(rating);
  }

  // State written before lifecycle IDs used the compact `<filmId>:<millis>`
  // fallback. Keep it comparable even after the live rating gains an ID.
  const filmId = String(rating?.filmId || rating?.id || "");
  const legacyPrefix = `${filmId}:`;
  const legacyTimestamp = String(originalKey).startsWith(legacyPrefix)
    ? Number(String(originalKey).slice(legacyPrefix.length))
    : Number.NaN;
  if (Number.isFinite(legacyTimestamp)) {
    return `${filmId}:${getDungeonRatingTime(rating)}`;
  }

  return getDungeonRatingLifecycleKey(rating);
};

export const getRevokedDungeonMovieIds = (stageProgress: DungeonStageProgress | null | undefined, ratings: Rating[] = []) => {
  if (stageProgress?.status !== "completed") return [];
  const matchedMovieIds = asArray(stageProgress.matchedMovieIds).map(String);
  const matchedRatingKeys = asArray(stageProgress.matchedRatingKeys).map(String);
  if (!matchedMovieIds.length) return [];

  const activeRatingsById = new Map(
    asArray(ratings)
      .filter((rating: Rating) => rating?.active !== false)
      .map((rating: Rating): [string, Rating] => [String(rating?.filmId || rating?.id || ""), rating])
      .filter(([filmId]) => Boolean(filmId))
  );

  return matchedMovieIds.filter((filmId, index) => {
    const activeRating = activeRatingsById.get(filmId);
    if (!activeRating) return true;
    const originalLifecycleKey = matchedRatingKeys[index];
    return Boolean(
      originalLifecycleKey &&
      getComparableDungeonLifecycleKey(activeRating, originalLifecycleKey) !== originalLifecycleKey
    );
  });
};

export const findRevokedDungeonStage = (stageProgress: DungeonStageProgress[] = [], ratings: Rating[] = []) => {
  return asArray(stageProgress).find((stage) =>
    getRevokedDungeonMovieIds(stage, ratings).length > 0
  ) || null;
};

export const isDungeonReconciliationAuthoritative = (
  reconciliation: Pick<DungeonReconciliation, "action" | "revokedMovieIds"> & { changed: boolean } | null | undefined,
  ratings: Rating[] = [],
  ratingHistory: RatingHistoryEntry[] = [],
  persistedState: WeeklyDungeonState | null = null
) => {
  if (!reconciliation?.changed) return true;
  const activeRatingIds = new Set(
    asArray(ratings)
      .filter((rating: Rating) => rating?.active !== false)
      .map((rating: Rating) => String(rating?.filmId || rating?.id || ""))
      .filter(Boolean)
  );
  const deletedRatingsById = new Map(
    asArray(ratingHistory)
      .filter((rating: Rating) => rating?.active === false)
      .map((rating: Rating): [string, Rating] => [String(rating?.filmId || rating?.id || ""), rating])
      .filter(([filmId]) => Boolean(filmId))
  );
  const stateUpdatedAt = Number(persistedState?.updatedAt) || 0;
  const matchedLifecycleKeys = new Map<string, string>();
  asArray(persistedState?.stageProgress).forEach((stage) => {
    asArray(stage?.matchedMovieIds).forEach((filmId, index) => {
      matchedLifecycleKeys.set(
        String(filmId),
        String(asArray(stage?.matchedRatingKeys)[index] || "")
      );
    });
  });

  return asArray(reconciliation.revokedMovieIds).every((filmIdValue) => {
    const filmId = String(filmIdValue);
    // A live rating means this is a lifecycle/metadata mismatch, not a
    // deletion. Never let an old tombstone authorize a rollback after the
    // film has already been rated again.
    if (activeRatingIds.has(filmId)) return false;

    const deletion = deletedRatingsById.get(filmId);
    if (!deletion) return false;
    const deletionUpdatedAt = getDungeonDeletionUpdatedTime(deletion);
    if (stateUpdatedAt && (!deletionUpdatedAt || deletionUpdatedAt <= stateUpdatedAt)) {
      return false;
    }

    const matchedKey = matchedLifecycleKeys.get(filmId) || "";
    const deletedLifecycleId = String(deletion?.ratingLifecycleId || "");
    if (
      matchedKey.startsWith(`id:${filmId}:`) &&
      deletedLifecycleId &&
      matchedKey !== `id:${filmId}:${deletedLifecycleId}`
    ) {
      return false;
    }

    return true;
  });
};

const arrayShallowEqual = <T>(left: T[] = [], right: T[] = []) =>
  left.length === right.length && left.every((item, index) => item === right[index]);

const textMatches = (values: string[], wantedValues: (string | number)[] = [], { allowPartial = true }: { allowPartial?: boolean } = {}) => {
  const wanted = asArray(wantedValues).map(normalizeText).filter(Boolean);
  return values.some((value) =>
    wanted.some((item) =>
      !allowPartial
        ? value === item
        : item.length <= 3 || value.length <= 3
        ? value === item
        : value === item || value.includes(item) || item.includes(value)
    )
  );
};

const conditionMatches = (rating: Rating, condition: DungeonCondition) => {
  if (!condition) return true;
  const year = getYear(rating);

  if (condition.type === "yearRange") {
    return Number(year) >= Number(condition.from) && Number(year) <= Number(condition.to);
  }
  if (condition.type === "decade") {
    return year ? Math.floor(year / 10) * 10 === Number(condition.value) : false;
  }
  if (condition.type === "genre") {
    return textMatches(getGenres(rating), [condition.value, ...(condition.aliases || [])], { allowPartial: false });
  }
  if (condition.type === "country") {
    return textMatches(getCountries(rating), [condition.value, ...(condition.aliases || [])]);
  }
  if (condition.type === "countryGroup") {
    return getCountries(rating).some((country) => countryBelongsToGroup(country, String(condition.value)));
  }
  if (condition.type === "director") {
    return personMatchesCondition(getRatingDirectors(rating), condition);
  }
  if (condition.type === "actor") {
    return personMatchesCondition(getRatingActors(rating), condition);
  }
  if (condition.type === "composer") {
    return personMatchesCondition(getRatingComposers(rating), condition);
  }
  return false;
};

export const ratingMatchesDungeonStage = (rating: Rating, stage: DungeonStageCriteria | null | undefined) => {
  if (!rating || !stage) return false;
  if (!isDungeonProgressEligible(rating)) return false;
  const conditions = asArray(stage.conditions);
  const mode = stage.match || "all";
  if (!conditions.length) return true;
  const matched = mode === "any"
    ? conditions.some((condition) => conditionMatches(rating, condition))
    : conditions.every((condition) => conditionMatches(rating, condition));
  return stage.invert ? !matched : matched;
};

const byRatingTime = (a: Rating, b: Rating) => getDungeonRatingTime(a) - getDungeonRatingTime(b);

export const getDungeonStageCompletionTime = (matches: Rating[] = [], requiredMoviesCount = 1, fallbackTime = Date.now()) => {
  const required = Math.max(1, Number(requiredMoviesCount) || 1);
  const fallback = Number(fallbackTime);
  const requiredMatches = asArray(matches).slice(0, required);
  const times = requiredMatches
    .map(getDungeonRatingTime)
    .filter((time) => Number.isFinite(time) && time > 0);

  if (times.length < required) {
    return Number.isFinite(fallback) ? fallback : Date.now();
  }

  const completedAt = Math.max(...times);
  return Number.isFinite(fallback) ? Math.min(completedAt, fallback) : completedAt;
};

export const getDungeonAcceptedMovieIds = (stageProgress: DungeonStageProgress = {}) => [
  ...new Set([
    ...asArray(stageProgress.acceptedMovieIds).map(String),
    ...asArray(stageProgress.matchedMovieIds).map(String)
  ].filter(Boolean))
];

export const getDungeonStageMatches = (
  stage: DungeonStageCriteria,
  ratings: Rating[] = [],
  startedAt: number | null | undefined,
  expiresAt: number | null | undefined,
  excludedMovieIds: EntityId[] = [],
  previouslyMatchedMovieIds: EntityId[] = []
) => {
  const start = Number(startedAt) || 0;
  const end = Number(expiresAt) || Number.MAX_SAFE_INTEGER;
  const excluded = new Set(excludedMovieIds.map(String));
  const previouslyMatched = new Set(asArray(previouslyMatchedMovieIds).map(String));
  const seen = new Set<string>();

  return asArray(ratings)
    .slice()
    .sort(byRatingTime)
    .filter((rating: Rating) => {
      const filmId = String(rating?.filmId || rating?.id || "");
      if (!filmId || seen.has(filmId) || excluded.has(filmId)) return false;
      // The stage deadline is exclusive: local Monday 00:00 already belongs
      // to the next ISO week and must not advance the previous expedition.
      if (!isDungeonRatingTimingEligible(rating, start, end, {
        allowPreviouslyMatched: previouslyMatched.has(filmId)
      })) return false;
      if (!ratingMatchesDungeonStage(rating, stage)) return false;
      seen.add(filmId);
      return true;
    });
};

// A rating is reserved by the first expedition stage that accepts it.  Later
// stages must not receive progress from the same film even when their
// conditions overlap (for example, South Korea is also part of Asia).
export const getDungeonStageExcludedMovieIds = (stageProgress: DungeonStageProgress[] = [], stageIndex: number | undefined) =>
  asArray(stageProgress)
    .slice(0, stageIndex)
    .flatMap(getDungeonAcceptedMovieIds)
    .map(String)
    .filter(Boolean);

const progressIdsAndKeysChanged = (progress: DungeonStageProgress, movieIds: EntityId[], ratingKeys: string[]) =>
  !arrayShallowEqual(asArray(progress?.matchedMovieIds).map(String), movieIds) ||
  !arrayShallowEqual(asArray(progress?.matchedRatingKeys).map(String), ratingKeys);

const buildCompletedStageFromMatches = (progress: DungeonStageProgress, definition: Pick<DungeonStageDefinition, "requiredMoviesCount"> | { requiredMoviesCount?: number }, matches: Rating[]): DungeonStageProgress & { matchedMovieIds: string[]; matchedRatingKeys: string[] } => {
  const required = Number(definition?.requiredMoviesCount || progress?.requiredMoviesCount) || 1;
  const completedMatches = matches.slice(0, required);
  const matchedMovieIds = completedMatches.map((rating: Rating) => String(rating?.filmId || rating?.id || ""));
  return {
    ...progress,
    status: "completed",
    requiredMoviesCount: required,
    completedMoviesCount: required,
    matchedMovieIds,
    acceptedMovieIds: [
      ...new Set([...getDungeonAcceptedMovieIds(progress), ...matchedMovieIds])
    ],
    matchedRatingKeys: completedMatches.map(getDungeonRatingLifecycleKey)
  };
};

export const reconcileDungeonStateAfterRatingDeletion = ({
  state = {},
  dungeon,
  ratings = [],
  now = Date.now()
}: { state?: WeeklyDungeonState | null; dungeon?: DungeonReconciliationDefinition | null; ratings?: Rating[]; now?: number } = {}): DungeonReconciliation | null => {
  if (!state || !dungeon || state.status === "failed") return null;
  const stageProgress = asArray(state.stageProgress);
  if (!stageProgress.length) return null;

  let changed = false;
  let revokedMovieIds: string[] = [];
  const nextProgress: DungeonStageProgress[] = [];

  for (let index = 0; index < stageProgress.length; index += 1) {
    const progress = stageProgress[index];
    const definition = dungeon.stages?.[index];
    if (!definition || progress?.status !== "completed") {
      nextProgress.push(progress);
      continue;
    }

    const missingMovieIds = getRevokedDungeonMovieIds(progress, ratings);
    if (!missingMovieIds.length) {
      nextProgress.push(progress);
      continue;
    }
    revokedMovieIds = [
      ...new Set([...revokedMovieIds, ...missingMovieIds])
    ];

    const excludedMovieIds = getDungeonStageExcludedMovieIds(nextProgress, index);
    const required = Number(definition.requiredMoviesCount || progress.requiredMoviesCount) || 1;
    const matches = getDungeonStageMatches(
      definition,
      ratings,
      progress.startedAt,
      progress.expiresAt,
      excludedMovieIds,
      getDungeonAcceptedMovieIds(progress)
    );

    if (matches.length >= required) {
      const rematchedProgress = buildCompletedStageFromMatches(progress, definition, matches);
      changed = changed || progressIdsAndKeysChanged(
        progress,
        rematchedProgress.matchedMovieIds,
        rematchedProgress.matchedRatingKeys
      );
      nextProgress.push(rematchedProgress);
      continue;
    }

    const expired = Number(now) >= Number(progress.expiresAt || 0);
    const partialMatches = matches.slice(0, Math.max(0, required - 1));
    const partialMovieIds = partialMatches.map((rating: Rating) => String(rating?.filmId || rating?.id || ""));
    const partialRatingKeys = partialMatches.map(getDungeonRatingLifecycleKey);
    const wasCompleted = state.status === "completed";

    if (expired) {
      const failedProgress: DungeonStageProgress[] = [
        ...nextProgress,
        {
          ...progress,
          status: "failed" as const,
          completedMoviesCount: partialMatches.length,
          matchedMovieIds: partialMovieIds,
          acceptedMovieIds: getDungeonAcceptedMovieIds(progress),
          matchedRatingKeys: partialRatingKeys,
          revokedAt: now
        },
        ...stageProgress.slice(index + 1).map((stage): DungeonStageProgress => ({
          ...stage,
          status: "failed",
          revokedAt: now
        }))
      ];
      return {
        changed: true,
        action: "failed",
        status: "failed",
        wasCompleted,
        revokedMovieIds,
        stageId: progress.stageId,
        state: {
          ...state,
          status: "failed",
          currentStageIndex: index,
          failedAt: now,
          revokedAt: now,
          failureReason: "rating_deleted",
          revokedMovieIds: [
            ...new Set([...asArray(state.revokedMovieIds).map(String), ...revokedMovieIds])
          ],
          updatedAt: now,
          stageProgress: failedProgress
        }
      };
    }

    const reopenedProgress: DungeonStageProgress = {
      ...progress,
      status: "in_progress",
      completedAt: null,
      completedMoviesCount: partialMatches.length,
      matchedMovieIds: partialMovieIds,
      acceptedMovieIds: getDungeonAcceptedMovieIds(progress),
      matchedRatingKeys: partialRatingKeys
    };

    return {
      changed: true,
      action: "rolled_back",
      status: "in_progress",
      wasCompleted,
      revokedMovieIds,
      stageId: progress.stageId,
      state: {
        ...state,
        status: "in_progress",
        completedAt: null,
        currentStageIndex: index,
        updatedAt: now,
        stageProgress: [...nextProgress, reopenedProgress]
      }
    };
  }

  if (!changed) return null;
  return {
    changed: true,
    action: "rematched",
    status: state.status,
    wasCompleted: false,
    revokedMovieIds,
    stageId: null,
    state: {
      ...state,
      updatedAt: now,
      stageProgress: nextProgress
    }
  };
};

const hashString = (value: string) => {
  let hash = 2166136261;
  for (const char of String(value)) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const seededRandom = (seed: string) => hashString(seed) / 4294967296;

const pickMany = <T extends { id: string }>(items: T[], count: number, seed: string): T[] => {
  const weighted = items
    .map((item) => ({ item, score: seededRandom(`${seed}:${item.id}`) }))
    .sort((a: { score: number; }, b: { score: number; }) => a.score - b.score);
  return weighted.slice(0, count).map(({ item }) => item);
};

const pickOne = <T extends { id: string }>(items: T[], seed: string): T => pickMany(items, 1, seed)[0];

const condition = (type: Exclude<DungeonCondition["type"], "yearRange">, value: string | number, label: string, extra: { aliases?: string[] } = {}): DungeonCondition => ({ type, value, label, ...extra });
const yearRange = (from: number, to: number): DungeonCondition => ({ type: "yearRange", value: `${from}-${to}`, label: `${from}-${to}`, from, to });
const genre = (value: string, label: string, aliases: string[] = []) => condition("genre", value, label || value, { aliases });
const country = (value: string, label: string, aliases: string[] = []) => condition("country", value, label || value, { aliases });
const group = (value: string, label: string) => condition("countryGroup", value, label);

interface DungeonTemplate {
  id: string;
  title: string;
  theme: string;
  stages: DungeonStageCriteria[];
  catalogValidation: DungeonCatalogValidation;
}
const template = (id: string, title: string, theme: string, stages: DungeonStageCriteria[], catalogValidation: DungeonCatalogValidation = {}): DungeonTemplate => ({
  id,
  title,
  theme,
  stages,
  catalogValidation
});

const DUNGEON_TEMPLATES = {
  common: [
    template("time_traveler", "Путешественник во времени", "Десятилетия и история кино", [
      { description: "Посмотрите фильм 1920-1950 годов.", conditions: [yearRange(1920, 1950)] },
      { description: "Посмотрите фильм 1951-1980 годов.", conditions: [yearRange(1951, 1980)] },
      { description: "Посмотрите 3 фильма 1981-2026 годов.", conditions: [yearRange(1981, 2026)] }
    ], { minKnownMovies: 80, sample: ["Метрополис", "Касабланка", "Крёстный отец", "Матрица"] }),
    template("genre_lab", "Жанровая лаборатория", "Базовые жанры", [
      { description: "Посмотрите драму.", conditions: [genre("драма", "драма", ["drama"])] },
      { description: "Посмотрите комедию.", conditions: [genre("комедия", "комедия", ["comedy"])] },
      { description: "Посмотрите 3 триллера.", conditions: [genre("триллер", "триллер", ["thriller"])] }
    ], { minKnownMovies: 120, sample: ["Побег из Шоушенка", "Назад в будущее", "Семь"] }),
    template("world_postcard", "Кинопочта мира", "Страны", [
      { description: "Посмотрите фильм из Франции.", conditions: [country("франция", "Франция", ["france", "fr"])] },
      { description: "Посмотрите фильм из Японии.", conditions: [country("япония", "Япония", ["japan", "jp"])] },
      { description: "Посмотрите 3 фильма из Европы.", conditions: [group("europe", "Европа")] }
    ], { minKnownMovies: 70, sample: ["Амели", "Унесённые призраками", "Жизнь прекрасна"] }),
    template("classic_route", "Классический маршрут", "Классика XX века", [
      { description: "Посмотрите фильм 1950-х.", conditions: [condition("decade", 1950, "1950-е")] },
      { description: "Посмотрите фильм 1960-х.", conditions: [condition("decade", 1960, "1960-е")] },
      { description: "Посмотрите 3 фильма 1970-х.", conditions: [condition("decade", 1970, "1970-е")] }
    ], { minKnownMovies: 60, sample: ["12 разгневанных мужчин", "Психо", "Таксист"] }),
    template("asian_gate", "Ворота Азии", "Азиатское кино", [
      { description: "Посмотрите фильм из Японии.", conditions: [country("япония", "Япония", ["japan", "jp"])] },
      { description: "Посмотрите фильм из Южной Кореи.", conditions: [country("южная корея", "Южная Корея", ["south korea", "korea", "kr"])] },
      { description: "Посмотрите 3 фильма из Азии.", conditions: [group("asia", "Азия")] }
    ], { minKnownMovies: 80, sample: ["Семь самураев", "Паразиты", "Любовное настроение"] }),
    template("new_hollywood", "Новый Голливуд", "Американское кино", [
      { description: "Посмотрите фильм США до 1980 года.", conditions: [country("сша", "США", ["usa", "us", "united states"]), yearRange(1930, 1979)] },
      { description: "Посмотрите фильм США 1980-2000 годов.", conditions: [country("сша", "США", ["usa", "us", "united states"]), yearRange(1980, 2000)] },
      { description: "Посмотрите 3 фильма США после 2000 года.", conditions: [country("сша", "США", ["usa", "us", "united states"]), yearRange(2001, 2026)] }
    ], { minKnownMovies: 120, sample: ["Таксист", "Форрест Гамп", "Социальная сеть"] })
  ],
  epic: [
    template("korean_thrillers", "Корейский нерв", "Корейские триллеры", [
      { description: "Посмотрите 3 южнокорейских фильма.", conditions: [country("южная корея", "Южная Корея", ["south korea", "korea", "kr"])] },
      { description: "Посмотрите 5 триллеров.", conditions: [genre("триллер", "триллер", ["thriller"])] },
      { description: "Посмотрите 8 фильмов из Азии.", conditions: [group("asia", "Азия")] }
    ], { minKnownMovies: 40, sample: ["Олдбой", "Воспоминания об убийстве", "Паразиты"] }),
    template("space_odyssey", "Космическая одиссея", "Космос и фантастика", [
      { description: "Посмотрите 3 фантастических фильма.", conditions: [genre("фантастика", "фантастика", ["science fiction", "sci-fi", "sci fi"])] },
      { description: "Посмотрите 5 фильмов после 1980 года.", conditions: [yearRange(1981, 2026)] },
      { description: "Посмотрите 8 фантастических фильмов.", conditions: [genre("фантастика", "фантастика", ["science fiction", "sci-fi", "sci fi"])] }
    ], { minKnownMovies: 70, sample: ["Чужой", "Интерстеллар", "Прибытие"] }),
    template("european_canvas", "Европейское полотно", "Европейское кино", [
      { description: "Посмотрите 3 европейских фильма.", conditions: [group("europe", "Европа")] },
      { description: "Посмотрите 5 драм.", conditions: [genre("драма", "драма", ["drama"])] },
      { description: "Посмотрите 8 европейских фильмов.", conditions: [group("europe", "Европа")] }
    ], { minKnownMovies: 90, sample: ["Амели", "Пианист", "Жизнь других"] }),
    template("animation_rite", "Обряд анимации", "Анимация", [
      { description: "Посмотрите 3 мультфильма.", conditions: [genre("мультфильм", "мультфильм", ["animation"])] },
      { description: "Посмотрите 5 семейных фильмов.", conditions: [genre("семейный", "семейный", ["family"])] },
      { description: "Посмотрите 8 мультфильмов.", conditions: [genre("мультфильм", "мультфильм", ["animation"])] }
    ], { minKnownMovies: 80, sample: ["Король Лев", "История игрушек", "Унесённые призраками"] })
  ],
  legendary: [
    template("century_gate", "Врата века", "Классика и современность", [
      { description: "Посмотрите 5 фильмов до 1970 года.", conditions: [yearRange(1900, 1969)] },
      { description: "Посмотрите 8 фильмов 1970-1999 годов.", conditions: [yearRange(1970, 1999)] },
      { description: "Посмотрите 12 фильмов 2000-2026 годов.", conditions: [yearRange(2000, 2026)] }
    ], { minKnownMovies: 160, sample: ["Гражданин Кейн", "Апокалипсис сегодня", "Начало"] }),
    template("world_cinema_crown", "Корона мирового кино", "Глобальный кинематограф", [
      { description: "Посмотрите 5 европейских фильмов.", conditions: [group("europe", "Европа")] },
      { description: "Посмотрите 8 азиатских фильмов.", conditions: [group("asia", "Азия")] },
      { description: "Посмотрите 12 фильмов не из США.", conditions: [country("сша", "США", ["usa", "us", "united states"])], invert: true }
    ], { minKnownMovies: 180, sample: ["Паразиты", "Амели", "Семь самураев", "Пианист"] }),
    template("genre_crown", "Корона жанров", "Жанровый марафон", [
      { description: "Посмотрите 5 драм.", conditions: [genre("драма", "драма", ["drama"])] },
      { description: "Посмотрите 8 триллеров.", conditions: [genre("триллер", "триллер", ["thriller"])] },
      { description: "Посмотрите 12 фильмов жанров: драма, комедия, фантастика или мультфильм.", match: "any", conditions: [
        genre("драма", "драма", ["drama"]),
        genre("комедия", "комедия", ["comedy"]),
        genre("фантастика", "фантастика", ["science fiction", "sci-fi"]),
        genre("мультфильм", "мультфильм", ["animation"])
      ] }
    ], { minKnownMovies: 220, sample: ["Крёстный отец", "Семь", "Матрица", "История игрушек"] })
  ]
};

const stageWithRules = (rarity: DungeonRarity, stage: DungeonStageCriteria, index: number): DungeonStageDefinition => ({
  ...WEEKLY_DUNGEON_STAGE_RULES[rarity][index],
  ...stage,
  conditions: stage.conditions || [],
  status: "available"
});

const buildDungeon = (templateItem: DungeonTemplate, rarity: DungeonRarity, weekId: string): WeeklyDungeon => {
  const rarityMeta = WEEKLY_DUNGEON_RARITIES[rarity];
  return {
    id: `${weekId}_${rarity}_${normalizeId(templateItem.id)}`,
    templateId: templateItem.id,
    weekId,
    rarity,
    rarityLabel: rarityMeta.label,
    reward: rarityMeta.reward,
    title: templateItem.title,
    theme: templateItem.theme,
    catalogValidation: templateItem.catalogValidation,
    stages: templateItem.stages.map((stage, index) => stageWithRules(rarity, stage, index))
  };
};

export const generateGlobalWeeklyDungeons = (weekId = getCurrentWeekId()): GlobalWeeklyDungeons => {
  const commonTemplates = pickMany(DUNGEON_TEMPLATES.common, 3, `${weekId}:common`);
  const epicTemplate = pickOne(DUNGEON_TEMPLATES.epic, `${weekId}:epic`);
  const legendaryTemplate = pickOne(DUNGEON_TEMPLATES.legendary, `${weekId}:legendary`);

  return {
    weekId,
    generatorVersion: WEEKLY_DUNGEON_GENERATOR_VERSION,
    status: "active",
    startsAt: getWeekStart(),
    endsAt: getWeekEnd(),
    commonDungeons: commonTemplates.map((item) => buildDungeon(item, "common", weekId)),
    epicDungeon: buildDungeon(epicTemplate, "epic", weekId),
    legendaryDungeon: buildDungeon(legendaryTemplate, "legendary", weekId)
  };
};

export const getAllWeeklyDungeons = (globalDungeons: GlobalWeeklyDungeons | null | undefined) => [
  ...asArray(globalDungeons?.commonDungeons),
  ...(globalDungeons?.epicDungeon ? [globalDungeons.epicDungeon] : []),
  ...(globalDungeons?.legendaryDungeon ? [globalDungeons.legendaryDungeon] : [])
].map(normalizeWeeklyDungeonReward).filter((dungeon): dungeon is NonNullable<typeof dungeon> => dungeon !== null).sort((a, b) =>
  (WEEKLY_DUNGEON_RARITIES[a.rarity]?.order || 0) - (WEEKLY_DUNGEON_RARITIES[b.rarity]?.order || 0)
);

export const getLegendaryDungeonChance = (pityCounter = 0) =>
  Math.min(1, WEEKLY_DUNGEON_LEGENDARY_BASE_CHANCE + Math.max(0, Number(pityCounter) || 0) * WEEKLY_DUNGEON_LEGENDARY_PITY_STEP);

interface DungeonAvailabilityOptions { weekId: string; userId: string; globalDungeons: GlobalWeeklyDungeons | null; legendaryPityCounter?: number; }

export const rollWeeklyDungeonAvailability = ({ weekId, userId, globalDungeons, legendaryPityCounter = 0 }: DungeonAvailabilityOptions) => {
  const availableDungeonIds = asArray(globalDungeons?.commonDungeons).map((item) => item.id);
  const epicDropped = seededRandom(`${weekId}:${userId}:epic-dungeon`) < WEEKLY_DUNGEON_EPIC_CHANCE;
  const legendaryChance = getLegendaryDungeonChance(legendaryPityCounter);
  const legendaryDropped = seededRandom(`${weekId}:${userId}:legendary-dungeon`) < legendaryChance;

  if (epicDropped && globalDungeons?.epicDungeon?.id) availableDungeonIds.push(globalDungeons.epicDungeon.id);
  if (legendaryDropped && globalDungeons?.legendaryDungeon?.id) availableDungeonIds.push(globalDungeons.legendaryDungeon.id);

  return {
    availableDungeonIds,
    epicDropped,
    legendaryDropped,
    legendaryChanceUsed: legendaryChance,
    nextLegendaryPityCounter: legendaryDropped ? 0 : Math.max(0, Number(legendaryPityCounter) || 0) + 1
  };
};

export const buildInitialWeeklyDungeonState = ({ weekId, userId, globalDungeons, legendaryPityCounter = 0 }: DungeonAvailabilityOptions): WeeklyDungeonState => {
  const availability = rollWeeklyDungeonAvailability({ weekId, userId, globalDungeons, legendaryPityCounter });
  const now = Date.now();
  return {
    stateVersion: WEEKLY_DUNGEON_STATE_VERSION,
    weekId,
    availableDungeonIds: availability.availableDungeonIds,
    selectedDungeonId: "",
    selectedAt: null,
    status: "available",
    currentStageIndex: 0,
    legendaryChanceUsed: availability.legendaryChanceUsed,
    legendaryPityCounter,
    epicDropped: availability.epicDropped,
    legendaryDropped: availability.legendaryDropped,
    stageProgress: [],
    createdAt: now,
    updatedAt: now
  };
};

export const getDungeonStageProgressKey = (weekId: string, dungeonId: string, stageId: string) =>
  `${weekId}:${dungeonId}:${stageId}`;
